import type { FeedbackPrivacyValue } from '@/constants/review/FeedbackPrivacy';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { User } from '@/types/directory/User';
import type {
    ReviewAudience,
    ReviewEvaluatorSelection,
    ReviewEvaluatorSelectionUpdate,
} from '@/types/review/ReviewAudience';

import { Feedback360SettingsLock } from '@/constants/review/Feedback360Settings';
import { getFeedback360SettingsLock } from '@/utils/cycleInstance';

export type Reviewee = User & {
    reviewers: User[];
    reviewAudienceAssignmentId: string;
    /** The saved reviewers a launched anonymous 360 keeps; one added since, not yet saved, can still go. */
    lockedReviewerIds?: string[];
};

interface GetRevieweesTransformedDataParams {
    reviewAudience?: ReviewAudience;
    reviewEvaluatorSelection?: ReviewEvaluatorSelection;
    reviewees?: User[];
    users?: User[];
}

export const getRevieweeIdsList = (
    reviewEvaluatorSelection: ReviewEvaluatorSelection,
): string[] => {
    const revieweeIds = (reviewEvaluatorSelection.reviewEvaluatorAssignments ?? [])
        .map((assignment) => assignment.reviewAudienceAssignment?.assignedTo)
        .filter((id): id is string => id !== undefined);

    return [...new Set(revieweeIds)];
};

export const getReviewerIdsList = (
    reviewEvaluatorSelection: ReviewEvaluatorSelection,
): string[] => {
    const reviewerIds = (reviewEvaluatorSelection.reviewEvaluatorAssignments ?? []).map(
        (assignment) => assignment.assignedTo,
    );

    return [...new Set(reviewerIds)];
};

export const getRevieweesTransformedData = ({
    reviewAudience,
    reviewEvaluatorSelection,
    reviewees,
    users,
}: GetRevieweesTransformedDataParams): Reviewee[] => {
    if (!reviewAudience || !reviewees) {
        return [];
    }

    const evaluatorAssignments = reviewEvaluatorSelection?.reviewEvaluatorAssignments ?? [];

    return reviewees.map((reviewee) => {
        const reviewAudienceAssignment = reviewAudience.reviewAudienceAssignments.find(
            (assignment) => assignment.assignedTo === reviewee.id,
        );
        const reviewAudienceAssignmentId = reviewAudienceAssignment?.id ?? '';

        const reviewerAssignments = evaluatorAssignments.filter(
            (assignment) => assignment.reviewAudienceAssignmentId === reviewAudienceAssignmentId,
        );

        const reviewerIds = reviewerAssignments.map((assignment) => assignment.assignedTo);
        const reviewers = users?.filter((user) => reviewerIds.includes(user.id)) ?? [];

        return {
            ...reviewee,
            reviewers,
            reviewAudienceAssignmentId,
        };
    });
};

export const getReviewees = ({
    reviewEvaluatorSelection,
    users,
}: GetRevieweesTransformedDataParams): User[] => {
    if (!reviewEvaluatorSelection || !users) {
        return [];
    }

    const revieweeIds = getRevieweeIdsList(reviewEvaluatorSelection);

    return users.filter((user) => revieweeIds.includes(user.id));
};

interface BuildReviewEvaluatorSelectionParams {
    cycleInstance?: CycleInstance;
    reviewerAssignments: Pick<Reviewee, 'reviewers' | 'reviewAudienceAssignmentId'>[];
    feedbackPrivacy: FeedbackPrivacyValue;
    commentRepliesEnabled: boolean;
}

/** The whole respondent list of a 360, as the server replaces it, with its confidentiality and replies to comments. */
export const buildReviewEvaluatorSelection = ({
    cycleInstance,
    reviewerAssignments,
    feedbackPrivacy,
    commentRepliesEnabled,
}: BuildReviewEvaluatorSelectionParams): ReviewEvaluatorSelectionUpdate => {
    const reviewEvaluatorSelectionId = cycleInstance?.reviewEvaluatorSelection?.id || '';

    const selection = {
        id: reviewEvaluatorSelectionId,
        cycleInstanceId: cycleInstance?.id || '',
        reviewEvaluatorAssignments: reviewerAssignments.flatMap((assignment) =>
            assignment.reviewers.map((reviewer) => ({
                assignedTo: reviewer.id,
                reviewEvaluatorSelectionId,
                reviewAudienceAssignmentId: assignment.reviewAudienceAssignmentId,
            })),
        ),
        createdAt: cycleInstance?.reviewEvaluatorSelection?.createdAt || '',
        updatedAt: new Date().toISOString(),
    };

    // A setting is left out once it is not this step's to set, so the server keeps it: the confidentiality
    // from launch on, and both settings of a 360 that follows its cycle or is over. Replies to comments change
    // until then.
    const lock = getFeedback360SettingsLock(cycleInstance);
    if (lock === Feedback360SettingsLock.INHERITED || lock === Feedback360SettingsLock.COMPLETED) {
        return selection;
    }
    return lock
        ? { ...selection, commentRepliesEnabled }
        : { ...selection, feedbackPrivacy, commentRepliesEnabled };
};

interface SubmittedAnswersParams {
    reviewEvaluatorSelection?: ReviewEvaluatorSelection;
    reviewAudienceAssignmentId: string;
    reviewerId: string;
}

/** Whether the respondent already submitted their answers about this reviewee, as the server last told. */
export const hasSubmittedAnswers = ({
    reviewEvaluatorSelection,
    reviewAudienceAssignmentId,
    reviewerId,
}: SubmittedAnswersParams): boolean =>
    (reviewEvaluatorSelection?.reviewEvaluatorAssignments ?? []).some(
        (assignment) =>
            assignment.reviewAudienceAssignmentId === reviewAudienceAssignmentId &&
            assignment.assignedTo === reviewerId &&
            assignment.hasSubmitted === true,
    );

/** The respondents picked since the 360 was last saved, each once, whichever reviewee they were picked for. */
export const getAddedReviewers = (
    reviewerAssignments: { revieweeId: string; reviewers: User[] }[],
    savedReviewees: Pick<Reviewee, 'id' | 'reviewers'>[],
): User[] => {
    const added = new Map<string, User>();
    reviewerAssignments.forEach(({ revieweeId, reviewers }) => {
        const saved = savedReviewees.find((reviewee) => reviewee.id === revieweeId)?.reviewers;
        reviewers
            .filter((reviewer) => !saved?.some((known) => known.id === reviewer.id))
            .forEach((reviewer) => added.set(reviewer.id, reviewer));
    });

    return [...added.values()];
};
